/**
 * norse-promo-banner-timer.js
 * Countdown for product promo banner blocks.
 *
 * Two sources, chosen with `data-timer-source`:
 *
 *   end_of_day  time left in the SHOPPER's day. Always HH:MM:SS — MM:SS cannot
 *               express twenty hours — never persisted, and it rolls straight into
 *               the next day at midnight instead of expiring.
 *   duration    the original evergreen countdown from `data-duration`, with its end
 *               time persisted in localStorage per block id.
 *   until       the Promotion's sale end in the visitor's region, `data-ends-at` in
 *               epoch ms (snippets/pdp-promotion-banner.liquid). HH:MM:SS, with days
 *               from 24 hours up ("6d 09:20:11").
 *   daily       the Promotion's daily reset: to the next midnight in the region's
 *               zone (`data-zone`), restarting until the sale ends.
 *   Both Promotion sources use assets/promo-countdown.js and hide the banner once
 *   the sale is over.
 *
 * The same end-of-day rule is implemented for the collection banner in
 * sections/promo-banner.liquid; keep the two in step.
 */
(function () {
  function pad(n) {
    return String(Math.floor(n)).padStart(2, '0');
  }

  /**
   * `new Date()` and the Date constructor are both local, so this is midnight as the
   * shopper experiences it in their own timezone — not the store's.
   */
  function secondsUntilEndOfDay() {
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
    return Math.max(0, Math.floor((midnight - now) / 1000));
  }

  function formatMmSs(totalSeconds) {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return pad(m) + ':' + pad(s);
  }

  function formatHms(totalSeconds) {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return pad(h) + ':' + pad(m) + ':' + pad(s);
  }

  function initTimer(el) {
    const blockId = el.dataset.blockId;
    const duration = parseInt(el.dataset.duration, 10);
    const timerEl = el.querySelector('.norse-promo-banner-block__timer');
    const hideOnExpire = el.dataset.hideOnExpire === 'true';
    const source = el.dataset.timerSource || 'duration';

    if (!timerEl || !blockId) return;

    if (source === 'end_of_day') {
      const render = () => {
        timerEl.textContent = formatHms(secondsUntilEndOfDay());
      };
      render();
      // Never cleared and never hidden: at midnight the remaining time rolls over to
      // the following day, so there is nothing to expire.
      setInterval(render, 1000);
      return;
    }

    if (source === 'until' || source === 'daily') {
      const countdown = window.NorsePromoCountdown;
      if (!countdown) return;
      let promoId;
      const renderPromo = () => {
        const left = countdown.secondsLeft(source, el.dataset.endsAt, el.dataset.zone);
        if (left === null) {
          clearInterval(promoId);
          el.style.display = 'none';
          return false;
        }
        timerEl.textContent = countdown.format(left);
        return true;
      };
      if (renderPromo()) promoId = setInterval(renderPromo, 1000);
      return;
    }

    const format = el.dataset.timerFormat || 'mmss';
    if (!duration) return;

    const lsKey = 'norse_promo_banner_timer_' + blockId;
    const now = Date.now();
    let endTime = parseInt(localStorage.getItem(lsKey) || '0', 10);

    if (!endTime || endTime <= now) {
      endTime = now + duration * 1000;
      try {
        localStorage.setItem(lsKey, String(endTime));
      } catch (_) {}
    }

    let intervalId;

    function tick() {
      const remaining = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
      timerEl.textContent = format === 'hms' ? formatHms(remaining) : formatMmSs(remaining);

      if (remaining <= 0) {
        clearInterval(intervalId);
        try {
          localStorage.removeItem(lsKey);
        } catch (_) {}
        if (hideOnExpire) {
          el.style.display = 'none';
        }
      }
    }

    tick();
    intervalId = setInterval(tick, 1000);
  }

  function attachAll() {
    document.querySelectorAll('.norse-promo-banner-block[data-block-id]').forEach(function (el) {
      if (el._norsePromoBannerTimerInit) return;
      // A Promotion countdown needs promo-countdown.js. If this script ran first
      // (an earlier copy on the page), leave the banner for the pass on `load`.
      const source = el.dataset.timerSource;
      if ((source === 'until' || source === 'daily') && !window.NorsePromoCountdown) return;
      el._norsePromoBannerTimerInit = true;
      initTimer(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachAll);
  } else {
    attachAll();
  }
  window.addEventListener('load', attachAll);
})();
